import { useState, useEffect, useMemo } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { useAuth } from '../lib/AuthContext';
import LZString from 'lz-string';
import { parseAxeResults, getComplianceLabel, VIOLATION_COSTS, getHowToFix } from '../lib/parseResults';

const SEVERITY_STYLES = {
    critical: {
        bg: 'bg-[var(--surface-elevated)]',
        border: 'border-[var(--error)]',
        badge: 'border-2 border-[var(--error)] text-[var(--error)] shadow-[0.25em_0.25em_0em_var(--border)] bg-[var(--error)]/5',
        text: 'text-[var(--error)]',
        icon: '🚫',
    },
    serious: {
        bg: 'bg-[var(--surface-elevated)]',
        border: 'border-orange-500',
        badge: 'border-2 border-orange-500 text-orange-500 shadow-[0.25em_0.25em_0em_var(--border)] bg-orange-500/5',
        text: 'text-orange-500',
        icon: '⚠️',
    },
    moderate: {
        bg: 'bg-[var(--surface-elevated)]',
        border: 'border-[var(--warning)]',
        badge: 'border-2 border-[var(--warning)] text-[var(--warning)] shadow-[0.25em_0.25em_0em_var(--border)] bg-[var(--warning)]/5',
        text: 'text-[var(--warning)]',
        icon: '⚡',
    },
    minor: {
        bg: 'bg-[var(--surface-elevated)]',
        border: 'border-[var(--accent)]',
        badge: 'border-2 border-[var(--accent)] text-[var(--accent)] shadow-[0.25em_0.25em_0em_var(--border)] bg-[var(--accent)]/5',
        text: 'text-[var(--accent)]',
        icon: '🔹',
    },
};

/**
 * Generates an AI-friendly "vibe coding" prompt for fixing a violation.
 */
function generateFixPrompt(violation) {
    const uniqueUrls = new Set(violation.nodes.map(n => n.pageUrl).filter(Boolean));
    const urlListing = uniqueUrls.size > 0
        ? `\n**Affected Pages:**\n${Array.from(uniqueUrls).map((url, i) => `  ${i + 1}. ${url}`).join('\n')}`
        : '';

    const nodeSnippets = violation.nodes
        .map((n, i) => `  ${i + 1}. ${n.html}`)
        .join('\n');

    return `Fix this WCAG 2.1 AA accessibility violation in my code:

**Issue:** ${violation.description}
**Rule ID:** ${violation.id}
**Severity:** ${violation.impact || 'unknown'}
${urlListing}

**Affected HTML elements (${violation.nodeCount} total):**
${nodeSnippets}

**How to Fix:** ${violation.help || violation.howToFix}

Please update the affected elements to be WCAG 2.1 Level AA compliant. Show me the corrected HTML/JSX.`;
}

/**
 * Single node detail — shows HTML, CSS selector, and failure summary
 */
function NodeDetail({ node, index, trackingData = {}, onToggleFixed, violationId, impact, help }) {
    const key = `${violationId}|${node.target}`;
    const isFixed = trackingData[key]?.status === 'fixed';

    return (
        <div className={`bg-[var(--surface-elevated)] border border-[var(--border)] p-5 space-y-3 rounded-2xl transition-all group/node ${isFixed ? 'opacity-40 bg-[var(--surface)] grayscale' : 'hover:border-[var(--accent)]/30'}`}>
            <div className="flex items-center gap-4 flex-wrap">
                <div
                    onClick={() => onToggleFixed && onToggleFixed({ key, violationId, impact, help })}
                    className={`w-5 h-5 border-2 rounded flex items-center justify-center cursor-pointer transition-all ${isFixed ? 'bg-[var(--success)] border-[var(--success)] text-black' : 'border-[var(--border)] hover:border-[var(--accent)]'}`}
                    title={isFixed ? "Mark as Pending" : "Mark as Fixed"}
                >
                    {isFixed && <span className="text-[0.625rem] font-black">✓</span>}
                </div>
                <span className="text-[0.625rem] text-[var(--accent)] font-black font-mono shrink-0 bg-[var(--surface)] px-2 py-0.5 rounded border border-[var(--border)]">#{(index + 1).toString().padStart(3, '0')}</span>
                {node.pageUrl && (
                    <a
                        href={node.pageUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-[0.5625rem] px-3 py-1 rounded-2xl bg-[var(--surface-elevated)] text-[var(--text-muted)] font-black uppercase tracking-widest truncate max-w-xs border border-[var(--border)] hover:text-[var(--accent)] hover:border-[var(--accent)] transition-colors"
                        title={node.pageUrl}
                    >
                        SOURCE: {new URL(node.pageUrl).pathname || node.pageUrl}
                    </a>
                )}
                {node.target && (
                    <p className="text-[0.5625rem] text-[var(--text-muted)] font-mono truncate font-black max-w-[12.5em] opacity-40 group-hover/node:opacity-100 transition-opacity" title={node.target}>
                        {node.target}
                    </p>
                )}
                {node.deviceProfile && node.deviceProfile !== 'desktop' && (
                    <span className="text-[0.5625rem] px-3 py-1 rounded-2xl font-black uppercase tracking-[0.2em] bg-[var(--accent)]/10 text-[var(--accent)] border border-[var(--accent)]/30 ml-auto">
                        SYST: {node.deviceProfile === 'mobile' ? 'MOBILE_SAFARI' : node.deviceProfile === 'tablet' ? 'TABLET_CHROME' : node.deviceProfile.toUpperCase()}
                    </span>
                )}
            </div>
            <code
                tabIndex="0"
                aria-label="HTML snippet"
                className="block text-xs bg-[var(--surface-elevated)] rounded-2xl p-5 text-[var(--accent)] overflow-x-auto whitespace-pre-wrap break-all border-2 border-[var(--border)] shadow-[inset_0_0_1.25em_rgba(0,0,0,0.5)] font-mono leading-relaxed group-hover/node:border-[var(--accent)]/30 transition-all"
            >
                {node.html}
            </code>
            {node.failureSummary && (
                <div className="text-xs text-[var(--text-muted)] space-y-2 pt-2">
                    <p className="font-bold text-[var(--accent)] uppercase tracking-wider opacity-60 flex items-center gap-2">
                        <span className="w-1 h-1 bg-[var(--accent)] rounded-2xl"></span>
                        Failure Details
                    </p>
                    <p className="whitespace-pre-line font-black leading-relaxed opacity-95">{node.failureSummary}</p>
                </div>
            )}
        </div>
    );
}

function ViolationCard({ violation, severity, trackingData, onToggleFixed, onRescanIssue }) {
    const style = SEVERITY_STYLES[severity] || SEVERITY_STYLES.minor;
    const [showAllNodes, setShowAllNodes] = useState(false);
    const [showPrompt, setShowPrompt] = useState(false);
    const [copied, setCopied] = useState(false);

    // Group nodes by URL
    const nodesByUrl = violation.nodes.reduce((acc, node) => {
        const url = node.pageUrl || 'Global / Unknown Pages';
        if (!acc[url]) acc[url] = [];
        acc[url].push(node);
        return acc;
    }, {});

    const sortedUrls = Object.keys(nodesByUrl).sort((a, b) => {
        if (a === 'Global / Unknown Pages') return 1;
        if (b === 'Global / Unknown Pages') return -1;
        return nodesByUrl[b].length - nodesByUrl[a].length; // Sort by number of issues descending
    });

    const isSinglePageScan = sortedUrls.length === 1 && sortedUrls[0] !== 'Global / Unknown Pages';

    const prompt = generateFixPrompt(violation);

    const handleCopyPrompt = () => {
        navigator.clipboard.writeText(prompt);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    return (
        <div className={`bg-[var(--surface-elevated)] border-2 border-[var(--border)] p-6 space-y-4 rounded-2xl hover:border-[var(--accent)]/50 transition-all group/card print:break-inside-avoid print:bg-transparent print:border-gray-300 print:text-black`}>
            <div className="flex items-start justify-between gap-5">
                <div className="min-w-0 flex-1">
                    <h4 className="text-[var(--text)] print:text-black font-bold text-sm leading-relaxed uppercase tracking-wider">
                        {violation.help}
                    </h4>
                    <div className="mt-3 flex items-center gap-3">
                        <p className="text-xs font-medium text-[var(--text-muted)] bg-[var(--surface)] px-3 py-1.5 rounded-2xl border border-[var(--border)] inline-block">
                            Violation ID: <span className="text-[var(--text)] font-mono">{violation.id}</span>
                        </p>
                    </div>
                </div>
                <div className="flex flex-col gap-2 shrink-0">
                    <span className={`text-xs px-4 py-2 rounded-2xl font-bold uppercase tracking-wider print:border print:border-gray-800 print:text-black ${style.badge}`}>
                        {violation.nodeCount} DETECTED
                    </span>
                    {onRescanIssue && (
                        <button
                            onClick={() => onRescanIssue(violation)}
                            className="text-[0.625rem] font-black uppercase tracking-widest text-[var(--accent)] hover:opacity-80 transition-all flex items-center justify-end gap-2 px-2"
                            title="Queue targeted rescan for this issue"
                        >
                            ⚡ Validate Fix
                        </button>
                    )}
                </div>
            </div>

            {/* Affected nodes grouped by URL */}
            <div className="space-y-4 pt-4">
                {sortedUrls.map(url => (
                    <details
                        key={url}
                        className="group bg-[var(--surface-elevated)] rounded-2xl overflow-hidden border border-[var(--border)]"
                        open={isSinglePageScan} // Auto-open if it's the only URL
                    >
                        <summary className="p-4 bg-[var(--surface-elevated)]/50 text-[0.625rem] font-black text-[var(--text-muted)] flex items-center justify-between cursor-pointer hover:bg-[var(--accent)]/5 transition-all group-open:border-b border-[var(--border)] uppercase tracking-[0.2em]">
                            <span className="flex items-center gap-3 truncate pr-4">
                                <span className="group-open:rotate-90 transition-transform text-[var(--accent)] font-mono">▶</span>
                                {url === 'Global / Unknown Pages' ? (
                                    <span className="truncate opacity-90 group-hover:opacity-100">{url}</span>
                                ) : (
                                    <a
                                        href={url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        onClick={(e) => e.stopPropagation()}
                                        className="truncate opacity-90 group-hover:opacity-100 hover:text-[var(--accent)] hover:underline transition-colors"
                                        title={url}
                                    >
                                        {new URL(url).pathname || url}
                                    </a>
                                )}
                            </span>
                            <span className="shrink-0 bg-[var(--surface-elevated)] border border-[var(--border)] px-3 py-1 rounded-2xl text-[0.5625rem] text-[var(--accent)] font-black">
                                {nodesByUrl[url].length} INSTANCE{nodesByUrl[url].length !== 1 ? 'S' : ''}
                            </span>
                        </summary>
                        <div className="p-5 space-y-4 bg-[var(--surface-elevated)] border-l-4 border-[var(--accent)]/20 ml-2 my-4">
                            {nodesByUrl[url].map((node, i) => (
                                <NodeDetail
                                    key={i}
                                    node={node}
                                    index={i}
                                    trackingData={trackingData}
                                    onToggleFixed={onToggleFixed}
                                    onRescanIssue={onRescanIssue}
                                    violationId={violation.id}
                                    impact={violation.impact || severity}
                                    help={violation.help}
                                />
                            ))}
                        </div>
                    </details>
                ))}
            </div>

            {/* How to Fix */}
            <div className="border-t-2 border-[var(--border)] pt-6 print:border-gray-200">
                <p className="text-[0.625rem] font-black text-[var(--text-muted)] uppercase tracking-[0.3em] mb-4 print:text-gray-600 flex items-center gap-3">
                    <span className="w-1.5 h-1.5 bg-[var(--success)] rounded-2xl"></span>
                    Remediation Plan
                </p>
                <div className="bg-[var(--surface)] border-2 border-[var(--success)]/20 p-5 rounded-2xl">
                    <p className="text-sm text-[var(--text)] leading-relaxed print:text-black whitespace-pre-line">
                        {violation.howToFix}
                    </p>
                </div>
                {violation.helpUrl && (
                    <a
                        href={violation.helpUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center mt-5 text-[0.625rem] font-black uppercase tracking-widest text-[var(--accent)] hover:text-[var(--text)] transition-colors gap-2 bg-[var(--surface-elevated)] px-4 py-2 border border-[var(--border)] hover:border-[var(--accent)] print:text-blue-600"
                    >
                        📚 View Full Documentation →
                    </a>
                )}
            </div>

            {/* AI Fix Prompt (Hidden in Print) */}
            <div className="border-t-2 border-[var(--border)] pt-6 print:hidden">
                <button
                    onClick={() => setShowPrompt(!showPrompt)}
                    className="flex items-center gap-3 text-[0.625rem] font-black uppercase tracking-[0.2em] text-[var(--accent)] hover:text-[var(--text)] transition-all cursor-pointer bg-[var(--surface-elevated)] px-5 py-3 rounded-2xl border border-[var(--border)] hover:border-[var(--accent)] shadow-sm"
                >
                    <span className="text-lg">🤖</span>
                    {showPrompt ? 'Stop AI Assistant' : 'Start AI Remediation'}
                </button>
                {showPrompt && (
                    <div className="mt-5 space-y-4 animate-in slide-in-from-top-4 duration-300">
                        <pre className="text-xs bg-[var(--surface)] border-2 border-[var(--accent)]/30 p-6 text-[var(--accent)] overflow-x-auto whitespace-pre-wrap rounded-2xl font-mono shadow-sm leading-loose">
                            {prompt}
                        </pre>
                        <button
                            onClick={handleCopyPrompt}
                            className={`px-8 py-3 rounded-2xl text-[0.625rem] font-black uppercase tracking-[0.2em] transition-all transform active:scale-95 ${copied
                                ? 'bg-[var(--success)] text-black shadow-sm'
                                : 'bg-[var(--accent)] text-black hover:shadow-sm'}`}
                        >
                            {copied ? '✓ Copied' : '📋 Copy Remediation'}
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}

/**
 * Renders a single privacy scan result
 */
function PrivacyCard({ finding }) {
    return (
        <div className="bg-[var(--surface-elevated)] border-2 border-[var(--border)] p-6 space-y-4 rounded-2xl hover:border-[var(--accent)]/50 transition-all group/card print:break-inside-avoid print:bg-transparent print:border-gray-300 print:text-black">
            <div className="flex items-start justify-between gap-5">
                <div className="min-w-0 flex-1">
                    <h4 className="text-[var(--text)] print:text-black font-black text-sm leading-relaxed uppercase tracking-wider flex items-center gap-2">
                        <span>🛡️</span> Privacy & Cookie Scan
                    </h4>
                    <div className="mt-2 text-[0.625rem] font-black uppercase tracking-widest text-[var(--accent)] break-all opacity-80">
                        {finding.url}
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                <div className="p-4 bg-[var(--surface)] border border-[var(--border)]">
                    <p className="text-[0.5625rem] text-[var(--text-muted)] font-black uppercase tracking-[0.2em] mb-2 opacity-60">Cookies Detected</p>
                    <p className="text-xl font-black font-mono tracking-tighter text-[var(--text)]">{finding.cookieCount}</p>
                </div>
                <div className="p-4 bg-[var(--surface)] border border-[var(--border)]">
                    <p className="text-[0.5625rem] text-[var(--text-muted)] font-black uppercase tracking-[0.2em] mb-2 opacity-60">CMP / Banner Context</p>
                    {finding.cmpDetected ? (
                        <p className="text-sm font-black text-[var(--success)] tracking-widest leading-relaxed">✓ {finding.cmpName || 'Recognized CMP'}</p>
                    ) : finding.bannerDetected ? (
                        <p className="text-sm font-black text-[var(--warning)] tracking-widest leading-relaxed">⚠️ Generic Banner Found</p>
                    ) : (
                        <p className="text-sm font-black text-[var(--error)] tracking-widest leading-relaxed flex items-center gap-2">
                            <span>🚫</span> NO BANNER CAPTURED
                        </p>
                    )}
                </div>
            </div>

            {finding.issues && finding.issues.length > 0 && (
                <div className="mt-6 space-y-3">
                    <p className="text-xs font-bold text-[var(--text-muted)] uppercase tracking-wider mb-4 print:text-gray-600 flex items-center gap-3">
                        <span className="w-1.5 h-1.5 bg-[var(--error)] rounded-2xl"></span>
                        Flags Detected
                    </p>
                    {finding.issues.map((issue, i) => (
                        <div key={i} className="bg-[var(--error)]/5 border border-[var(--error)]/30 p-4 rounded-2xl">
                            <h5 className="text-sm font-bold text-[var(--error)] mb-2">{issue.title}</h5>
                            <p className="text-xs text-[var(--text)] opacity-80 leading-relaxed font-medium">{issue.description}</p>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

/**
 * Generates CSV string from parsed results
 */
function generateCSV(groups) {
    const rows = [['Severity', 'Rule ID', 'Description', 'Element Count', 'Page URL', 'CSS Selector', 'HTML Snippet', 'How to Fix']];

    groups.forEach((group) => {
        group.violations.forEach((v) => {
            v.nodes.forEach((node) => {
                rows.push([
                    group.label,
                    v.id,
                    `"${v.description.replace(/"/g, '""')}"`,
                    v.nodeCount,
                    `"${(node.pageUrl || '').replace(/"/g, '""')}"`,
                    `"${node.target.replace(/"/g, '""')}"`,
                    `"${node.html.replace(/"/g, '""')}"`,
                    `"${(v.howToFix || '').replace(/"/g, '""')}"`,
                ]);
            });
        });
    });

    return rows.map((r) => r.join(',')).join('\n');
}

/**
 * Downloads a string as a file
 */
function downloadFile(content, filename, mimeType) {
    const blob = new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
}

const COMPLIANCE_INFO = {
    'state-local': {
        label: 'State / Local Government',
        description: 'ADA Title II & Section 508',
        icon: '🏛️',
        legalBasis: 'Title II of the Americans with Disabilities Act (ADA) and Section 508 of the Rehabilitation Act.',
        standard: 'WCAG 2.1 Level AA'
    },
    'federal': {
        label: 'Federal / Federally Funded',
        description: 'Section 508 & WCAG 2.1 AA',
        icon: '🇺🇸',
        legalBasis: 'Section 504 and Section 508 of the Rehabilitation Act.',
        standard: 'WCAG 2.1 Level AA'
    },
    'private': {
        label: 'Private Business',
        description: 'ADA Title III & WCAG 2.1',
        icon: '🏢',
        legalBasis: 'Title III of the Americans with Disabilities Act (ADA).',
        standard: 'WCAG 2.1 Level AA'
    }
};

export default function ScanResultsView({
    results: initialResults,
    scanUrl,
    parentScanId,
    claimedAgentInfo,
    complianceType,
    isHA,
    thisScanId,
    isReportSnapshot = false,
    reportData = null,
    onDownloadPDF = null,
    onClose = null,
    trackingData = {},
    onToggleFixed = null,
    onRescanIssue = null
}) {
    const { user } = useAuth();
    const [results, setResults] = useState(() => {
        if (typeof initialResults === 'string') {
            try {
                // Try decompressing first
                const decompressed = LZString.decompressFromUTF16(initialResults);
                if (decompressed) return JSON.parse(decompressed);
                return JSON.parse(initialResults);
            } catch (e) {
                console.error('[ScanResultsView] Failed to parse initial results string:', e);
                return [];
            }
        }
        return initialResults;
    });
    const [isDownloading, setIsDownloading] = useState(false);

    // Update results if initialResults changes
    useEffect(() => {
        if (typeof initialResults === 'string') {
            try {
                const decompressed = LZString.decompressFromUTF16(initialResults);
                if (decompressed) setResults(JSON.parse(decompressed));
                else setResults(JSON.parse(initialResults));
            } catch (e) {
                console.error('[ScanResultsView] Failed to parse updated results string:', e);
            }
        } else {
            setResults(initialResults);
        }
    }, [initialResults]);

    // Process results or use reportData
    const { groups, summary } = useMemo(() => {
        if (isReportSnapshot && reportData) {
            const reportGroups = [
                { impact: 'critical', label: 'Critical', violations: [] },
                { impact: 'serious', label: 'Serious', violations: [] },
                { impact: 'moderate', label: 'Moderate', violations: [] },
                { impact: 'minor', label: 'Minor', violations: [] }
            ];

            Object.entries(reportData.ruleStats || {}).forEach(([ruleId, data]) => {
                const group = reportGroups.find(g => g.impact === data.impact);
                if (group) {
                    group.violations.push({
                        id: ruleId,
                        description: data.description || ruleId,
                        help: data.help || data.description || ruleId,
                        impact: data.impact,
                        helpUrl: data.helpUrl || null,
                        howToFix: getHowToFix({ id: ruleId, help: data.help || data.description }),
                        nodeCount: data.count || data.totalCount || 0,
                        nodes: Object.entries(data.pages || {}).map(([url, count]) => ({
                            pageUrl: url,
                            html: `[SYSTEM_SIGNAL] ${count} instances identified on target. Granular source logs only available in direct audits.`,
                            target: `TARGET_CLUSTER: ${url}`
                        }))
                    });
                }
            });

            return {
                groups: reportGroups.filter(g => g.violations.length > 0),
                summary: {
                    ...reportData.summary,
                    detectedCMS: reportData.summary?.detectedCMS || reportData.detectedCMS || null,
                    totalPasses: reportData.summary?.totalPasses || 0,
                    totalIncomplete: reportData.summary?.totalIncomplete || 0,
                    totalAffectedNodes: reportData.summary?.totalAffectedNodes || reportData.summary?.totalViolations || 0,
                    bySeverity: reportData.summary?.bySeverity || {
                        critical: reportGroups.find(g => g.impact === 'critical')?.violations.length || 0,
                        serious: reportGroups.find(g => g.impact === 'serious')?.violations.length || 0,
                        moderate: reportGroups.find(g => g.impact === 'moderate')?.violations.length || 0,
                        minor: reportGroups.find(g => g.impact === 'minor')?.violations.length || 0,
                    }
                }
            };
        }
        return parseAxeResults(results);
    }, [results, isReportSnapshot, reportData]);

    const [parentResults, setParentResults] = useState(null);
    const [diffStats, setDiffStats] = useState(null);
    const [isPrinting, setIsPrinting] = useState(false);
    const [isAggregating, setIsAggregating] = useState(false);
    const [copyStatus, setCopyStatus] = useState({});

    // HA Aggregation Effect
    useEffect(() => {
        if (!user || !isHA || !thisScanId) return;

        setIsAggregating(true);
        const queueRef = collection(db, 'users', user.uid, 'queue');
        const q = query(queueRef, orderBy('createdAt', 'asc'));

        const unsubscribe = onSnapshot(q, (snap) => {
            const childScans = snap.docs
                .map(d => ({ id: d.id, ...d.data() }))
                .filter(s => s.parentScanId === thisScanId);

            if (childScans.length === 0) {
                // If no children but isHA, we might still be waiting for discovery
                setIsAggregating(false);
                return;
            }

            // Merge results
            let mergedResults = Array.isArray(initialResults) ? [...initialResults] : (initialResults ? [initialResults] : []);
            const scannedUrls = new Set(mergedResults.flatMap(r => r.url || []));

            childScans.forEach(child => {
                if (child.status === 'completed' && child.results) {
                    let childData = child.results;
                    if (typeof childData === 'string' && child.dataIsCompressed) {
                        try {
                            childData = JSON.parse(LZString.decompressFromUTF16(childData));
                        } catch (e) { console.error("Failed to decompress child results", e); return; }
                    }

                    const resultsArray = Array.isArray(childData) ? childData : [childData];
                    resultsArray.forEach(res => {
                        const urlToMatch = res.url || child.url;
                        if (urlToMatch && !scannedUrls.has(urlToMatch)) {
                            // Ensure the URL is explicitly set if it was missing in individual results
                            mergedResults.push({ ...res, url: urlToMatch });
                            scannedUrls.add(urlToMatch);
                        } else if (!urlToMatch && !scannedUrls.has('unknown')) {
                            mergedResults.push(res);
                            scannedUrls.add('unknown');
                        }
                    });
                }
            });

            setResults(mergedResults);
            setIsAggregating(false);

            // Sync the aggregated summary back to the parent scan doc
            // so the Dashboard list (collapsed cards) shows the total risk.
            if (mergedResults.length > 0) {
                const newSummary = parseAxeResults(mergedResults).summary;
                updateDoc(doc(db, 'users', user.uid, 'queue', thisScanId), {
                    summary: newSummary,
                    updatedAt: serverTimestamp()
                }).catch(e => console.error("Failed to sync aggregated summary:", e));
            }
        }, (err) => {
            console.error("HA Aggregation failed:", err);
            setIsAggregating(false);
        });

        return () => unsubscribe();
    }, [user, isHA, thisScanId, initialResults]);

    // Split into legal and best practice groups
    const legalGroups = groups.map(g => ({
        ...g,
        violations: g.violations.filter(v => !v.isBestPractice)
    })).filter(g => g.violations.length > 0);

    const bestPracticeGroups = groups.map(g => ({
        ...g,
        violations: g.violations.filter(v => v.isBestPractice)
    })).filter(g => g.violations.length > 0);

    // Count URLs scanned from results
    const urlsScanned = isReportSnapshot && reportData?.summary?.totalPages
        ? reportData.summary.totalPages
        : (Array.isArray(results)
            ? results.length
            : (results?.pagesCrawled || (() => {
                const urls = new Set();
                (results?.violations || []).forEach(v => (v.pageUrls || []).forEach(u => urls.add(u)));
                return urls.size || 1;
            })()));

    // Fetch Parent Scan for Mitigation Diffing
    useEffect(() => {
        if (!user || !parentScanId) return;

        const fetchParent = async () => {
            try {
                const parentDoc = await getDoc(doc(db, 'users', user.uid, 'queue', parentScanId));
                if (parentDoc.exists() && parentDoc.data().results) {
                    const pData = parentDoc.data();
                    setParentResults(pData.results);

                    // Compute Diffs
                    const pParsed = parseAxeResults(pData.results);

                    // Simple Diff: Compare total violations
                    const currentTotal = summary.totalViolations;
                    const previousTotal = pParsed.summary.totalViolations;

                    // Complex Diff: Count specific nodes resolved vs missing
                    // We look at the unique CSS selectors (targets) for each violation
                    const currentTargets = new Set();
                    groups.forEach(g => g.violations.forEach(v => v.nodes.forEach(n => currentTargets.add(n.target))));

                    const previousTargets = new Set();
                    pParsed.groups.forEach(g => g.violations.forEach(v => v.nodes.forEach(n => previousTargets.add(n.target))));

                    let resolvedCount = 0;
                    previousTargets.forEach(t => {
                        if (!currentTargets.has(t)) resolvedCount++;
                    });

                    let newCount = 0;
                    currentTargets.forEach(t => {
                        if (!previousTargets.has(t)) newCount++;
                    });

                    setDiffStats({
                        previousTotal,
                        currentTotal,
                        resolvedCount,
                        newCount,
                        netChange: currentTotal - previousTotal
                    });
                }
            } catch (err) {
                console.error('Failed to load parent scan for diff:', err);
            }
        };

        fetchParent();
    }, [user, parentScanId, summary.totalViolations]);

    if (!results && !isReportSnapshot) {
        return (
            <div className="text-center p-8">
                <p className="neu-text-muted font-bold italic">No results available yet.</p>
            </div>
        );
    }

    // Use centralized risk metrics from summary
    const riskScore = summary.riskScore;
    const totalRiskCost = summary.totalRiskCost;

    const getRiskLevel = (score) => {
        if (score === 0) return { label: 'Excellent - No Issues', color: 'text-[var(--success)]', bg: 'bg-[var(--success)]', shadow: 'border-2 border-[var(--success)] shadow-sm', barColor: 'bg-[var(--success)]' };
        if (score <= 20) return { label: 'Low Risk', color: 'text-[var(--success)]', bg: 'bg-[var(--success)]', shadow: 'border-2 border-[var(--success)] shadow-sm', barColor: 'bg-[var(--success)]' };
        if (score <= 50) return { label: 'Medium Risk', color: 'text-[var(--warning)]', bg: 'bg-[var(--warning)]', shadow: 'border-2 border-[var(--warning)] shadow-sm', barColor: 'bg-[var(--warning)]' };
        if (score <= 75) return { label: 'High Risk', color: 'text-orange-500', bg: 'bg-orange-500', shadow: 'border-2 border-orange-500 shadow-sm', barColor: 'bg-orange-500' };
        return { label: 'Critical Risk', color: 'text-[var(--error)]', bg: 'bg-[var(--error)]', shadow: 'border-2 border-[var(--error)] shadow-sm', barColor: 'bg-[var(--error)]' };
    };
    const riskLevel = getRiskLevel(riskScore);

    // Unruh Act stacking calculations ($4,000 per distinct infraction)
    const totalInfractions = summary.totalViolations;
    const totalAffectedElements = summary.totalAffectedNodes;
    const perVisitExposure = totalInfractions * 4000; // Each unique violation = $4k
    const multiVisitExposure = perVisitExposure * 3; // 3 visits = 3× (aggressive plaintiff model)
    const multiPlaintiffExposure = perVisitExposure * 3; // 3 different plaintiffs

    const handleExportJSON = () => {
        downloadFile(JSON.stringify(results, null, 2), `a11y-scan-${Date.now()}.json`, 'application/json');
    };

    const handleExportCSV = () => {
        const csv = generateCSV(groups);
        downloadFile(csv, `a11y-scan-${Date.now()}.csv`, 'text/csv');
    };

    const handleCopyFixes = async (severity = null) => {
        let selectedViolations = [];
        let title = "all";

        if (severity) {
            const group = groups.find(g => g.impact === severity);
            if (group) {
                selectedViolations = group.violations.map(v => ({ ...v, impact: severity }));
                title = severity.toUpperCase();
            }
        } else {
            selectedViolations = groups.flatMap(g =>
                g.violations.map(v => ({ ...v, impact: g.impact }))
            );
        }

        if (selectedViolations.length === 0) return;

        const prompts = selectedViolations.map(v => generateFixPrompt(v));
        const combined = `I have ${selectedViolations.length} WCAG 2.1 AA accessibility violations (${title}) on ${scanUrl || 'my page'}. Please fix all of them:\n\n${prompts.join('\n\n---\n\n')}`;

        try {
            await navigator.clipboard.writeText(combined);
            setCopyStatus(prev => ({ ...prev, [severity || 'all']: true }));
            setTimeout(() => {
                setCopyStatus(prev => ({ ...prev, [severity || 'all']: false }));
            }, 2000);
        } catch (err) {
            console.error("Failed to copy:", err);
            // Fallback for older browsers
            const textarea = document.createElement('textarea');
            textarea.value = combined;
            document.body.appendChild(textarea);
            textarea.select();
            document.execCommand('copy');
            document.body.removeChild(textarea);
            setCopyStatus(prev => ({ ...prev, [severity || 'all']: true }));
            setTimeout(() => {
                setCopyStatus(prev => ({ ...prev, [severity || 'all']: false }));
            }, 2000);
        }
    };

    const handlePrint = () => {
        document.body.classList.add('printing-report');
        setTimeout(() => {
            window.print();
            document.body.classList.remove('printing-report');
        }, 100);
    };

    const handleDownload = async () => {
        if (!onDownloadPDF) return;
        setIsDownloading(true);
        try {
            // If it's a report snapshot, we already have the structured data
            if (isReportSnapshot) {
                await onDownloadPDF({
                    id: thisScanId || 'snapshot',
                    name: `Accessibility_Report_${new Date().toLocaleDateString()}`,
                    summary: summary,
                    ruleStats: reportData?.ruleStats,
                    criteria: { domain: scanUrl || 'System' }
                });
            } else {
                // For a direct scan, we need to construct the ruleStats from the results
                // handleDownloadPDF in DashboardPage expects these fields
                const ruleStats = {};
                groups.forEach(g => {
                    g.violations.forEach(v => {
                        ruleStats[v.id] = {
                            impact: v.impact || g.impact,
                            description: v.description,
                            count: v.nodeCount,
                            pagesCount: new Set(v.nodes.map(n => n.pageUrl)).size || 1
                        };
                    });
                });

                await onDownloadPDF({
                    id: thisScanId || 'temp',
                    name: `Scan_Audit_${new Date().toLocaleDateString()}`,
                    summary: summary,
                    ruleStats: ruleStats,
                    criteria: { domain: scanUrl || 'Direct Scan' }
                });
            }
        } catch (err) {
            console.error("PDF Download failed:", err);
        } finally {
            setIsDownloading(false);
        }
    };

    return (
        <div id="scan-report-print" className="space-y-6 print:space-y-4 print:text-black scroll-mt-24">

            {/* Print Only Header */}
            <div className="hidden print:block mb-8 border-b border-gray-300 pb-4">
                <h1 className="text-2xl font-bold">ADA Compliance Scan Report</h1>
                <p className="text-gray-600 mt-1">Target: {scanUrl || 'Unknown URL'}</p>
                <p className="text-gray-600">Date: {new Date().toLocaleString()}</p>
            </div>

            {/* Quick Actions Bar (Top) */}
            <div className="flex flex-wrap items-center justify-between gap-6 bg-[var(--surface-elevated)] p-6 rounded-2xl border-2 border-[var(--border)] print:hidden shadow-sm  animate-in slide-in-from-top duration-500">
                <div className="flex items-center gap-4">
                    <div className="p-2 bg-[var(--accent)]/10 rounded-2xl border border-[var(--accent)]/30">
                        <span className="text-xl">🤖</span>
                    </div>
                    <div>
                        <h4 className="text-sm font-bold text-[var(--accent)]">Remediation Assistant</h4>
                        <div className="flex items-center gap-2 mt-1">
                            <p className="text-xs text-[var(--text-muted)] opacity-70">AI-powered fix generation available</p>
                            {summary.detectedCMS && (
                                <span className="text-xs px-2 py-0.5 bg-[var(--accent)]/10 text-[var(--accent)] border border-[var(--accent)]/30 rounded-full ml-2">
                                    {summary.detectedCMS}
                                </span>
                            )}
                        </div>
                    </div>
                </div>
                <button
                    onClick={() => handleCopyFixes()}
                    className={`bg-[var(--accent)] text-black font-black text-[0.625rem] uppercase tracking-[0.2em] px-10 py-3 rounded-2xl hover:shadow-sm transition-all cursor-pointer transform active:scale-95 flex items-center gap-3 ${copyStatus.all ? 'bg-[var(--success)] shadow-sm' : ''}`}
                >
                    <span className="text-sm">{copyStatus.all ? '✓' : '📋'}</span>
                    {copyStatus.all ? 'ALL_REMEDIATION_PROMPTS_COPIED' : 'COPY_ALL_AI_FIX_PROMPTS'}
                </button>
            </div>

            {/* Compliance Context & Legal Disclaimer */}
            {/* Compliance Context & Legal Disclaimer */}
            <div className="bg-[var(--surface-elevated)] border-2 border-[var(--border)] p-10 rounded-2xl shadow-sm overflow-hidden relative group">
                <div className="absolute top-0 right-0 w-32 h-32 bg-[var(--accent)]/5 blur-3xl rounded-2xl"></div>
                <div className="flex flex-col lg:flex-row gap-12 relative z-10">
                    <div className="flex-1 space-y-8">
                        <div className="flex items-center gap-6">
                            <span className="text-4xl">{COMPLIANCE_INFO[complianceType]?.icon || '📋'}</span>
                            <div>
                                <h3 className="text-[var(--accent)] font-black text-xl uppercase tracking-[0.3em] ">
                                    {COMPLIANCE_INFO[complianceType]?.label?.toUpperCase() || 'ACCESSIBILITY_STANDARD'}
                                </h3>
                                <p className="text-[0.5625rem] text-[var(--text-muted)] font-black uppercase tracking-[0.4em] mt-1 opacity-50">Automated Scan v1.0.4</p>
                            </div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            <div className="p-5 rounded-2xl bg-[var(--surface-elevated)] border border-[var(--border)] group-hover:border-[var(--accent)]/30 transition-all">
                                <p className="text-[0.5625rem] font-black uppercase text-[var(--text-muted)] tracking-[0.3em] mb-3 opacity-60 flex items-center gap-2">
                                    <span className="w-1 h-1 bg-[var(--accent)] rounded-2xl"></span>
                                    Compliance Level
                                </p>
                                <p className="text-sm font-black text-[var(--text)] tracking-widest">{COMPLIANCE_INFO[complianceType]?.standard || 'WCAG 2.1 LEVEL AA'}</p>
                            </div>
                            <div className="p-5 rounded-2xl bg-[var(--surface-elevated)] border border-[var(--border)] group-hover:border-[var(--accent)]/30 transition-all">
                                <p className="text-[0.5625rem] font-black uppercase text-[var(--text-muted)] tracking-[0.3em] mb-3 opacity-60 flex items-center gap-2">
                                    <span className="w-1 h-1 bg-[var(--accent)] rounded-2xl"></span>
                                    Legal Framework
                                </p>
                                <p className="text-sm font-black text-[var(--text)] tracking-widest leading-relaxed">{COMPLIANCE_INFO[complianceType]?.legalBasis || 'ADA & DIGITAL ACCESSIBILITY ACT'}</p>
                            </div>
                            {summary.performance && (
                                <div className="p-5 rounded-2xl bg-[var(--accent)]/5 border border-[var(--accent)]/20 group-hover:border-[var(--accent)] transition-all">
                                    <p className="text-[0.5625rem] font-black uppercase text-[var(--accent)] tracking-[0.3em] mb-3 flex items-center gap-2">
                                        <span className="w-1 h-1 bg-[var(--accent)] rounded-2xl"></span>
                                        Performance Analytics
                                    </p>
                                    <div className="flex flex-col gap-2">
                                        <p className="text-[0.625rem] font-bold text-[var(--text)] tracking-widest">
                                            TOTAL_TIME: {(summary.performance.totalDurationMs / 1000 / 60).toFixed(1)}m
                                        </p>
                                        <div className="flex gap-4">
                                            <div className="flex flex-col">
                                                <span className="text-[0.5rem] text-[var(--text-muted)] font-black uppercase">Low</span>
                                                <span className="text-[0.625rem] font-bold text-[var(--success)]">{(summary.performance.pageMetrics?.min / 1000).toFixed(2)}s</span>
                                            </div>
                                            <div className="flex flex-col">
                                                <span className="text-[0.5rem] text-[var(--text-muted)] font-black uppercase">High</span>
                                                <span className="text-[0.625rem] font-bold text-[var(--error)]">{(summary.performance.pageMetrics?.max / 1000).toFixed(2)}s</span>
                                            </div>
                                            <div className="flex flex-col">
                                                <span className="text-[0.5rem] text-[var(--text-muted)] font-black uppercase">Avg</span>
                                                <span className="text-[0.625rem] font-bold text-[var(--accent)]">{(summary.performance.pageMetrics?.avg / 1000).toFixed(2)}s</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                    <div className="lg:w-[25em] p-8 rounded-2xl border-2 border-[var(--error)]/20 bg-[var(--error)]/5 relative overflow-hidden">
                        <div className="absolute top-0 left-0 w-1 h-full bg-gradient-to-b from-transparent via-[var(--error)] to-transparent opacity-50"></div>
                        <h4 className="text-[var(--error)] font-black text-[0.75rem] uppercase tracking-[0.3em] mb-4 flex items-center gap-3">
                            <span className="text-lg">⚠️</span> Disclaimer: Automated Scan
                        </h4>
                        <p className="text-[0.6875rem] leading-loose text-[var(--text-muted)] font-black uppercase tracking-widest italic opacity-95">
                            Automated analysis detected. Remediation support only.
                            <span className="text-[var(--error)] "> NON-DEFINITIVE RESULTS.</span>
                            ADA / 508 liability persists post-scan. Manual audit recommended for full compliance.
                        </p>
                    </div>
                </div>
            </div>

            {/* Mitigation Diff Banner */}
            {diffStats && (
                <div className="bg-[var(--surface)] border-2 border-[var(--accent)] p-8 rounded-2xl shadow-sm relative overflow-hidden group/diff animate-in zoom-in-95 duration-500">
                    <div className="absolute inset-0 bg-gradient-to-r from-[var(--accent)]/5 to-transparent opacity-20 pointer-events-none"></div>
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-8 relative z-10">
                        <div>
                            <div className="flex items-center gap-4 mb-2">
                                <div className="p-2 bg-[var(--accent)]/10 rounded-2xl border border-[var(--accent)]/30">
                                    <span className="text-2xl">🔄</span>
                                </div>
                                <h3 className="text-[var(--accent)] font-black text-xl uppercase tracking-[0.3em] ">
                                    Remediation Progress
                                </h3>
                            </div>
                            <p className="text-[0.625rem] text-[var(--text-muted)] font-black uppercase tracking-[0.2em] mt-3 bg-[var(--surface-elevated)] px-3 py-1.5 rounded-2xl border border-[var(--border)] inline-block">
                                Baseline: {diffStats.previousTotal.toString().padStart(3, '0')} Issues Previously Detected
                            </p>
                        </div>
                        <div className="flex items-center gap-10">
                            {diffStats.resolvedCount > 0 && (
                                <div className="text-center group-hover/diff:scale-110 transition-transform duration-500">
                                    <p className="text-[var(--success)] font-black text-5xl font-mono tracking-tighter">{diffStats.resolvedCount}</p>
                                    <p className="text-[0.5625rem] text-[var(--text-muted)] font-black uppercase tracking-[0.3em] mt-2 opacity-60">Issues Resolved</p>
                                </div>
                            )}
                            <div className="w-px h-16 bg-[var(--border)] opacity-20" />
                            {diffStats.newCount > 0 ? (
                                <div className="text-center">
                                    <p className="text-orange-500 font-bold text-5xl font-mono tracking-tighter">{diffStats.newCount}</p>
                                    <p className="text-xs text-[var(--text-muted)] mt-2 opacity-70">New Issues</p>
                                </div>
                            ) : (
                                <div className="text-center">
                                    <p className="text-[var(--accent)] font-bold text-4xl tracking-tighter ">Clear</p>
                                    <p className="text-xs text-[var(--text-muted)] mt-2 opacity-70">Status</p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* ===== URL Evidence Section ===== */}
            <details className="bg-[var(--surface-elevated)] border-2 border-[var(--border)] rounded-2xl overflow-hidden group/evidence transition-all">
                <summary className="p-8 flex items-center justify-between cursor-pointer hover:bg-[var(--accent)]/5 transition-all">
                    <div className="flex items-center gap-6">
                        <div className="p-3 bg-[var(--accent)]/10 rounded-2xl border border-[var(--accent)]/30 group-hover/evidence:border-[var(--accent)] transition-colors">
                            <span className="text-3xl">📄</span>
                        </div>
                        <div>
                            <h3 className="text-[var(--accent)] font-bold text-xl uppercase tracking-wider ">
                                Evidence Links: Scanned URLs
                            </h3>
                            <p className="text-xs text-[var(--text-muted)] font-medium mt-2 opacity-90">
                                Scanned URL List // {isHA ? 'Distributed Sync Active' : 'Standalone Scan'}
                            </p>
                        </div>
                    </div>
                    <div className="flex items-center gap-6">
                        {isAggregating && (
                            <span className="text-[var(--accent)] text-[0.625rem] font-black uppercase tracking-widest animate-pulse">Syncing...</span>
                        )}
                        <span className="text-[var(--accent)] font-mono text-xl group-open/evidence:rotate-180 transition-transform">▼</span>
                    </div>
                </summary>
                <div className="p-8 border-t border-[var(--border)] bg-[var(--surface-elevated)]">
                    <div className="overflow-x-auto rounded-2xl border border-[var(--border)] shadow-sm">
                        <table className="w-full text-left border-collapse font-mono text-[0.6875rem] uppercase tracking-widest leading-relaxed">
                            <thead className="bg-[var(--surface-elevated)] text-[var(--text-muted)] opacity-60">
                                <tr>
                                    <th className="px-6 py-4 border-b border-[var(--border)]">ID</th>
                                    <th className="px-6 py-4 border-b border-[var(--border)]">Scanned URL</th>
                                    <th className="px-6 py-4 border-b border-[var(--border)]">Status</th>
                                    <th className="px-6 py-4 border-b border-[var(--border)]">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-[var(--border)]/10">
                                {(() => {
                                    const urls = new Set();

                                    if (isReportSnapshot && reportData?.ruleStats) {
                                        Object.values(reportData.ruleStats).forEach(data => {
                                            Object.keys(data.pages || {}).forEach(u => urls.add(u));
                                        });
                                    } else {
                                        const resultsList = Array.isArray(results) ? results : (results ? [results] : []);
                                        resultsList.forEach(r => {
                                            if (r.url) urls.add(r.url);
                                            // Some axe results might have pageUrl in blocks
                                            if (r.violations) {
                                                r.violations.forEach(v => v.nodes.forEach(n => {
                                                    if (n.pageUrl) urls.add(n.pageUrl);
                                                }));
                                            }
                                        });
                                    }

                                    const urlList = Array.from(urls);
                                    if (urlList.length === 0) {
                                        if (scanUrl) urlList.push(scanUrl);
                                        else return <tr><td colSpan="4" className="px-6 py-10 text-center italic opacity-30">No URLs scanned.</td></tr>;
                                    }

                                    return urlList.map((nodeUrl, idx) => (
                                        <tr key={idx} className="hover:bg-[var(--accent)]/5 transition-colors group/row">
                                            <td className="px-6 py-4 text-[var(--accent)] font-black">#{(idx + 1).toString().padStart(3, '0')}</td>
                                            <td className="px-6 py-4 text-[var(--text)] truncate max-w-xs" title={nodeUrl}>{nodeUrl}</td>
                                            <td className="px-6 py-4 text-[var(--success)] font-black opacity-80">STABLE</td>
                                            <td className="px-6 py-4">
                                                <a href={nodeUrl} target="_blank" rel="noopener noreferrer" className="text-[var(--accent)] hover:text-[var(--text)] transition-colors underline underline-offset-4 decoration-[var(--accent)]/30">[VIEW PAGE]</a>
                                            </td>
                                        </tr>
                                    ));
                                })()}
                            </tbody>
                        </table>
                    </div>
                </div>
            </details>

            {/* ===== Privacy & Compliance Section ===== */}
            {summary.privacy && summary.privacy.length > 0 && (() => {
                const totalPrivacyIssues = summary.privacy.reduce((sum, finding) => sum + (finding.issues?.length || 0), 0);

                return (
                    <div className="mt-16 space-y-8 animate-in slide-in-from-bottom-8 duration-700">
                        <div className="flex items-center gap-6 border-b-2 border-dashed border-[var(--border)] pb-6">
                            <span className="text-3xl filter grayscale opacity-50">🔒</span>
                            <div>
                                <h2 className="text-2xl font-bold text-[var(--accent)] uppercase tracking-wider ">
                                    Privacy & Compliance Checks
                                </h2>
                                <p className="text-xs text-[var(--text-muted)] font-medium mt-2 opacity-70">Cookie Banner & Consent Architecture Scan</p>
                            </div>
                        </div>

                        {totalPrivacyIssues > 0 ? (
                            <div className="grid grid-cols-1 gap-6">
                                {summary.privacy.map((finding, idx) => (
                                    <PrivacyCard key={idx} finding={finding} />
                                ))}
                            </div>
                        ) : (
                            <div className="bg-[var(--surface-elevated)] border-2 border-[var(--success)]/30 p-10 rounded-2xl flex items-center justify-between shadow-sm group/privacy-success overflow-hidden relative">
                                <div className="absolute top-0 right-0 w-32 h-32 bg-[var(--success)]/5 blur-3xl rounded-full"></div>
                                <div className="flex items-center gap-8 relative z-10">
                                    <div className="w-20 h-20 bg-[var(--success)]/10 border-2 border-[var(--success)] rounded-2xl flex items-center justify-center text-4xl font-black text-[var(--success)] shadow-[0.25em_0.25em_0em_rgba(21,128,61,0.2)]">
                                        A
                                    </div>
                                    <div>
                                        <h3 className="text-[var(--success)] font-black text-xl uppercase tracking-tighter mb-1">Privacy Architecture Stable</h3>
                                        <p className="text-xs text-[var(--text-muted)] font-bold uppercase tracking-widest opacity-60">No distinct cookie leakage or consent bypass detected.</p>
                                    </div>
                                </div>
                                <div className="hidden md:block text-right relative z-10">
                                    <div className="text-[0.625rem] font-black text-[var(--success)] uppercase tracking-[0.3em] bg-[var(--success)]/10 px-4 py-2 rounded-full border border-[var(--success)]/20">
                                        TRUST_LEVEL: HIGH
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                );
            })()}

            {/* ===== Risk Score & Exposure Dashboard ===== */}
            <div className={`bg-[var(--surface-elevated)] border-2 rounded-2xl overflow-hidden ${riskLevel.shadow} print:border-gray-800 print:bg-transparent transition-all duration-500`}>
                {/* Risk Score Header */}
                <div className="p-10 flex flex-col lg:flex-row lg:items-center justify-between gap-10 bg-[var(--surface-elevated)]/30 ">
                    <div className="flex items-center gap-10">
                        <div className="text-center bg-[var(--surface-elevated)] p-6 rounded-2xl border border-[var(--border)] shadow-sm min-w-[8.75em]">
                            <p className={`text-6xl font-bold font-mono tracking-tighter ${riskLevel.color} print:text-black`}>{riskScore.toString().padStart(2, '0')}</p>
                            <p className="text-xs text-[var(--text-muted)] font-medium uppercase tracking-wider mt-3 opacity-70">Risk Level</p>
                        </div>
                        <div>
                            <h3 className={`font-bold text-2xl uppercase tracking-wider ${riskLevel.color}  print:text-black`}>{riskLevel.label}</h3>
                            <p className="text-[var(--text-muted)] font-medium text-xs mt-2 opacity-80">
                                {totalInfractions} Issue{totalInfractions !== 1 ? 's' : ''} Found // {totalAffectedElements} Affected Element{totalAffectedElements !== 1 ? 's' : ''}
                            </p>
                        </div>
                    </div>
                    <div className="lg:text-right px-8 py-5 bg-[var(--surface-elevated)] rounded-2xl border border-[var(--border)] group/exposure">
                        <p className="text-xs text-[var(--text-muted)] font-bold uppercase tracking-wider mb-2 opacity-60">Estimated Exposure</p>
                        <p className="text-5xl font-bold text-[var(--error)] font-mono tracking-tighter group-hover:scale-105 transition-transform duration-500">${perVisitExposure.toLocaleString()}</p>
                    </div>
                </div>
                {/* Risk Bar */}
                <div className="px-10 py-2 bg-[var(--surface-elevated)]">
                    <div className="h-4 bg-[var(--surface-elevated)] rounded-2xl overflow-hidden border border-[var(--border)] p-1">
                        <div className={`h-full ${riskLevel.barColor} rounded-2xl transition-all duration-1000 shadow-sm`} style={{ width: `${riskScore}%` }} />
                    </div>
                </div>

                {/* Stacking Exposure Grid */}
                <div className="grid grid-cols-1 md:grid-cols-3 border-t border-[var(--border)] bg-[var(--surface-elevated)]">
                    <div className="p-8 border-r border-[var(--border)] hover:bg-[var(--error)]/5 transition-colors">
                        <p className="text-xs text-[var(--text-muted)] font-bold uppercase tracking-wider mb-3 opacity-50 flex items-center gap-2">
                            <span className="w-1 h-1 bg-[var(--error)] rounded-2xl animate-ping"></span>
                            Analysis: Single Page
                        </p>
                        <p className="text-3xl font-bold text-[var(--error)] font-mono tracking-tighter">${perVisitExposure.toLocaleString()}</p>
                        <p className="text-xs text-[var(--text-muted)] font-medium mt-2 opacity-60">{totalInfractions} Issues × $4,000 per violation</p>
                    </div>
                    <div className="p-8 border-r border-[var(--border)] hover:bg-orange-500/5 transition-colors">
                        <p className="text-xs text-[var(--text-muted)] font-bold uppercase tracking-wider mb-3 opacity-50 flex items-center gap-2">
                            <span className="w-1 h-1 bg-orange-500 rounded-2xl animate-ping"></span>
                            Multi-Visit (3X)
                        </p>
                        <p className="text-3xl font-bold text-orange-500 font-mono tracking-tighter">${multiVisitExposure.toLocaleString()}</p>
                        <p className="text-xs text-[var(--text-muted)] font-medium mt-2 opacity-60">Persistent anomalies across sessions</p>
                    </div>
                    <div className="p-8 hover:bg-[var(--error)]/[0.08] transition-colors">
                        <p className="text-xs text-[var(--text-muted)] font-bold uppercase tracking-wider mb-3 opacity-50 flex items-center gap-2">
                            <span className="w-1 h-1 bg-red-600 rounded-2xl animate-ping"></span>
                            Multiple Plaintiff Risk
                        </p>
                        <p className="text-3xl font-bold text-red-600 font-mono tracking-tighter">${(multiPlaintiffExposure + multiVisitExposure).toLocaleString()}</p>
                        <p className="text-xs text-[var(--text-muted)] font-medium mt-2 opacity-60">Cumulative exposure from individual pages</p>
                    </div>
                </div>


                {/* Educational Infraction Stacking Section */}
                <details className="group border-t border-[var(--border)] print:border-gray-300 bg-[var(--surface-elevated)]">
                    <summary className="p-6 text-[0.6875rem] font-black text-[var(--text-muted)] flex items-center justify-between cursor-pointer hover:text-[var(--accent)] transition-all print:text-black uppercase tracking-[0.2em]">
                        <span className="flex items-center gap-4">
                            <span className="p-1 px-2 bg-[var(--accent)]/10 border border-[var(--accent)]/30 rounded text-xs">LOG</span>
                            Understanding Potential Exposure
                        </span>
                        <span className="text-[0.5625rem] opacity-40 group-open:hidden print:hidden">[ACCESS_ADDITIONAL_DATA]</span>
                    </summary>
                    <div className="px-8 pb-8 space-y-4 text-[0.6875rem] text-[var(--text-muted)] font-black uppercase tracking-widest leading-loose print:text-gray-700">
                        <div className="bg-[var(--surface-elevated)] p-5 border-2 border-[var(--border)] rounded-2xl shadow-sm">
                            <p className="text-[var(--error)] font-bold mb-3  text-xs tracking-wider flex items-center gap-2">
                                <span className="w-1.5 h-1.5 bg-[var(--error)] rounded-2xl"></span>
                                Definition: Violation
                            </p>
                            <p className="opacity-80">Any specific element failing WCAG 2.1 AA threshold. Examples: Null-alt images, Keyboard-trapped nodes, or inaccessible trigger-points. Each distinct failure constitutes a separate legal baseline.</p>
                        </div>
                        <div className="bg-[var(--surface-elevated)] p-5 border-2 border-[var(--border)] rounded-2xl shadow-sm">
                            <p className="text-orange-500 font-bold mb-3  text-xs tracking-wider flex items-center gap-2">
                                <span className="w-1.5 h-1.5 bg-orange-500 rounded-2xl"></span>
                                TEMPORAL STACKING: BY VISIT
                            </p>
                            <p className="opacity-80">Under Unruh Act, statutory minimums reach $4,000 per violation. Multi-visit scenarios multiply exposure — baseline attorneys argue each session re-triggers the civil rights breach sequence.</p>
                        </div>
                        <div className="bg-[var(--surface-elevated)] p-5 border-2 border-[var(--border)] rounded-2xl shadow-sm">
                            <p className="text-[var(--warning)] font-bold mb-3  text-xs tracking-wider flex items-center gap-2">
                                <span className="w-1.5 h-1.5 bg-[var(--warning)] rounded-2xl"></span>
                                STRUCTURAL STACKING: BY ISSUE
                            </p>
                            <p className="opacity-80">Encountering {totalInfractions} different accessibility issues in a single session allows for incremental stacking. Exposure scales linearly with the number of unique detected violations.</p>
                        </div>
                        <div className="bg-[var(--surface-elevated)] p-5 border-2 border-[var(--border)] rounded-2xl shadow-sm">
                            <p className="text-[var(--accent)] font-bold mb-3  text-xs tracking-wider flex items-center gap-2">
                                <span className="w-1.5 h-1.5 bg-[var(--accent)] rounded-2xl"></span>
                                Multiple Plaintiff Risk
                            </p>
                            <p className="opacity-80">Settlement with external nodes offers zero immunity against parallel plaintiffs. System remains live and vulnerable until 100% remediation is achieved on the production branch.</p>
                        </div>
                    </div>
                </details>
            </div>

            {/* Summary Bar - Clickable Anchors */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-6">
                {[
                    { label: 'CRITICAL', key: 'critical', count: summary.bySeverity.critical, color: 'text-[var(--error)] border-[var(--error)] hover:shadow-sm' },
                    { label: 'SERIOUS', key: 'serious', count: summary.bySeverity.serious, color: 'text-orange-500 border-orange-500 hover:shadow-sm' },
                    { label: 'MODERATE', key: 'moderate', count: summary.bySeverity.moderate, color: 'text-[var(--warning)] border-[var(--warning)] hover:shadow-sm' },
                    { label: 'MINOR', key: 'minor', count: summary.bySeverity.minor, color: 'text-[var(--accent)] border-[var(--accent)] hover:shadow-sm' },
                ].map(({ label, key, count, color }) => (
                    <div
                        key={label}
                        className={`relative bg-[var(--surface-elevated)] border-2 text-center transition-all hover:-translate-y-1 rounded-2xl group/stat print:border-gray-300 print:bg-transparent ${color}`}
                    >
                        <a
                            href={`#severity-${key}`}
                            className="block p-6 cursor-pointer"
                        >
                            <p className="text-4xl font-bold font-mono tracking-tighter print:text-black">{count.toString().padStart(2, '0')}</p>
                            <p className="text-xs font-medium mt-3 opacity-70 print:text-gray-600 uppercase tracking-wider">{label}</p>
                        </a>
                        {count > 0 && (
                            <button
                                onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    handleCopyFixes(key);
                                }}
                                className={`absolute bottom-2 right-2 p-2 rounded-2xl bg-[var(--surface-elevated)] border border-[var(--border)] hover:border-white transition-all text-[0.625rem] font-bold uppercase tracking-wider flex items-center gap-2 ${copyStatus[key] ? 'text-[var(--success)] border-[var(--success)] shadow-sm' : 'text-[var(--text-muted)]'}`}
                                title={`Copy all ${label.toLowerCase()} AI fixes`}
                            >
                                📋 {copyStatus[key] ? 'Copied' : 'Copy'}
                            </button>
                        )}
                    </div>
                ))}
            </div>

            {/* Export & Action Details Toolbar */}
            <div className="bg-[var(--surface-elevated)] border-2 border-[var(--border)] p-4 flex flex-wrap items-center justify-between gap-6 print:hidden rounded-2xl mt-8">
                <div className="flex flex-wrap items-center gap-6 text-xs font-bold text-[var(--text-muted)]">
                    <span className="flex items-center gap-2 opacity-80">⚡ {urlsScanned} Pages Scanned</span>
                    <span className="flex items-center gap-2 text-[var(--success)]">✓ {summary.totalPasses} Passed</span>
                    <span className="flex items-center gap-2 text-orange-500">⚠️ {summary.totalIncomplete} Incomplete</span>
                    <span className="flex items-center gap-2 text-[var(--accent)]">📍 {summary.totalAffectedNodes} Total Elements</span>
                </div>
                <div className="flex flex-wrap items-center gap-4">
                    <button
                        onClick={onDownloadPDF ? handleDownload : handlePrint}
                        disabled={isDownloading}
                        className="bg-[var(--accent)] text-black font-bold text-xs px-6 py-2 rounded-2xl hover:shadow-sm transition-all cursor-pointer transform active:scale-95 disabled:opacity-50 disabled:grayscale"
                    >
                        {isDownloading ? 'Generating PDF...' : 'Print PDF Report'}
                    </button>
                    <button
                        onClick={handleExportJSON}
                        className="bg-[var(--surface)] text-[var(--text-muted)] border-2 border-[var(--border)] font-bold text-xs px-6 py-2 rounded-2xl hover:border-[var(--accent)] hover:text-[var(--accent)] transition-all cursor-pointer transform active:scale-95"
                    >
                        JSON
                    </button>
                    <button
                        onClick={handleExportCSV}
                        className="bg-[var(--surface)] text-[var(--text-muted)] border-2 border-[var(--border)] font-bold text-xs px-6 py-2 rounded-2xl hover:border-[var(--accent)] hover:text-[var(--accent)] transition-all cursor-pointer transform active:scale-95"
                    >
                        CSV
                    </button>
                    {groups.length > 0 && (
                        <button
                            onClick={() => handleCopyFixes()}
                            className={`bg-[var(--surface)] text-[var(--accent)] border-2 border-[var(--accent)] font-bold text-xs px-6 py-2 rounded-2xl hover:shadow-sm transition-all cursor-pointer transform active:scale-95 flex items-center gap-2 ${copyStatus.all ? 'border-[var(--success)] text-[var(--success)] shadow-sm' : ''}`}
                        >
                            <span>🤖</span> {copyStatus.all ? '✓ Copied' : 'Batch Fix'}
                        </button>
                    )}
                </div>
            </div>

            {/* Legal Requirements Section */}
            <div className="mt-16 space-y-12">
                <div className="flex items-center gap-6 border-b-2 border-dashed border-[var(--border)] pb-6">
                    <span className="text-3xl filter grayscale opacity-50">🏛️</span>
                    <div>
                        <h2 className="text-2xl font-bold text-[var(--accent)] uppercase tracking-wider ">
                            Legal Scan Results
                        </h2>
                        <p className="text-xs text-[var(--text-muted)] font-medium mt-2 opacity-70">Authorized compliance layer v2.1 // ADA & Section 508</p>
                    </div>
                </div>
                {legalGroups.length === 0 ? (
                    <div className="bg-[var(--surface-elevated)] border-2 border-[var(--success)] p-12 rounded-2xl text-center shadow-sm relative overflow-hidden group/success">
                        <div className="absolute top-0 left-0 w-full h-1 bg-[var(--success)] shadow-sm"></div>
                        <p className="text-[var(--success)] text-3xl font-black uppercase tracking-[0.3em]">SIGNAL_CLEAR: ZERO_VIOLATIONS</p>
                        <p className="text-[0.625rem] text-[var(--text-muted)] font-black uppercase tracking-[0.2em] mt-4 opacity-70">
                            Automated analysis report indicates 100% compliance with accessibility standards.
                        </p>
                    </div>
                ) : (
                    legalGroups.map((group) => {
                        const style = SEVERITY_STYLES[group.impact] || SEVERITY_STYLES.minor;
                        return (
                            <div key={`legal-${group.impact}`} id={`severity-${group.impact}`} className="space-y-8 pt-6 scroll-mt-24">
                                <div className="flex items-center justify-between border-b border-[var(--border)] pb-4">
                                    <h3 className={`text-xl font-bold ${style.text} flex items-center gap-4 print:text-black uppercase tracking-wider `}>
                                        <span className="print:hidden p-2 bg-[var(--surface-elevated)] border border-[var(--border)] rounded-2xl">{style.icon}</span>
                                        {group.label} Integrity Risks
                                    </h3>
                                    <div className="flex items-center gap-4">
                                        <button
                                            onClick={() => handleCopyFixes(group.impact)}
                                            className={`print:hidden p-2 px-4 bg-[var(--surface-elevated)] border border-[var(--border)] rounded-2xl text-xs font-bold uppercase tracking-wider hover:border-[var(--accent)] transition-all flex items-center gap-2 ${copyStatus[group.impact] ? 'text-[var(--success)] border-[var(--success)] shadow-sm' : 'text-[var(--text-muted)]'}`}
                                        >
                                            🤖 {copyStatus[group.impact] ? 'Fixes Copied' : 'Copy AI Fixes'}
                                        </button>
                                        <span className="text-xs font-bold text-[var(--text-muted)] bg-[var(--surface-elevated)] px-4 py-2 rounded-2xl border border-[var(--border)] uppercase tracking-wider">
                                            Issues: {group.violations.length.toString().padStart(2, '0')}
                                        </span>
                                    </div>
                                </div>
                                <div className="grid grid-cols-1 gap-6">
                                    {group.violations.map((violation) => (
                                        <ViolationCard
                                            key={violation.id}
                                            violation={{ ...violation, impact: group.impact }}
                                            severity={group.impact}
                                            trackingData={trackingData}
                                            onToggleFixed={onToggleFixed}
                                            onRescanIssue={onRescanIssue}
                                        />
                                    ))}
                                </div>
                                <div className="text-right mt-6 print:hidden">
                                    <a href="#report-top" className="text-xs font-bold uppercase tracking-wider text-[var(--accent)] hover:text-[var(--text)] transition-colors bg-[var(--surface-elevated)] px-6 py-3 rounded-2xl border border-[var(--border)] inline-flex items-center gap-3 group/top">
                                        Back to top <span className="group-hover:-translate-y-1 transition-transform">↑</span>
                                    </a>
                                </div>
                            </div>
                        );
                    })
                )}
            </div>

            {/* Best Practices Section */}
            {bestPracticeGroups.length > 0 && (
                <div className="mt-24 space-y-12">
                    <div className="flex items-center gap-6 border-b-2 border-dashed border-[var(--border)] pb-6">
                        <span className="text-3xl filter grayscale opacity-50">💡</span>
                        <div>
                            <h2 className="text-2xl font-bold text-[var(--text)] uppercase tracking-wider opacity-80">
                                Recommended Optimizations
                            </h2>
                            <p className="text-xs text-[var(--text-muted)] font-medium mt-2 opacity-70">Best Practices // Improving Accessibility</p>
                        </div>
                    </div>
                    {bestPracticeGroups.map((group) => {
                        const style = SEVERITY_STYLES[group.impact] || SEVERITY_STYLES.minor;
                        return (
                            <div key={`bp-${group.impact}`} className="space-y-8 pt-6 scroll-mt-24">
                                <div className="flex items-center justify-between border-b border-[var(--border)] pb-4">
                                    <h3 className={`text-xl font-bold ${style.text} flex items-center gap-4 print:text-black uppercase tracking-wider `}>
                                        <span className="print:hidden p-2 bg-[var(--surface-elevated)] border border-[var(--border)] rounded-2xl">{style.icon}</span>
                                        {group.label} Enhancements
                                    </h3>
                                    <span className="text-xs font-bold text-[var(--text-muted)] bg-[var(--surface-elevated)] px-4 py-2 rounded-2xl border border-[var(--border)] uppercase tracking-wider">
                                        Issues: {group.violations.length.toString().padStart(2, '0')}
                                    </span>
                                </div>
                                <div className="grid grid-cols-1 gap-6">
                                    {group.violations.map((violation) => (
                                        <ViolationCard
                                            key={violation.id}
                                            violation={{ ...violation, impact: group.impact }}
                                            severity={group.impact}
                                            trackingData={trackingData}
                                            onToggleFixed={onToggleFixed}
                                            onRescanIssue={onRescanIssue}
                                        />
                                    ))}
                                </div>
                                <div className="text-right mt-6 print:hidden">
                                    <a href="#report-top" className="text-xs font-bold uppercase tracking-wider text-[var(--accent)] hover:text-[var(--text)] transition-colors bg-[var(--surface-elevated)] px-6 py-3 rounded-2xl border border-[var(--border)] inline-flex items-center gap-3 group/top">
                                        Back to top <span className="group-hover:-translate-y-1 transition-transform">↑</span>
                                    </a>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {onClose && (
                <div className="text-center py-24 border-t border-[var(--border)] mt-12 print:hidden">
                    <button onClick={onClose} className="bg-[var(--surface)] text-[var(--text)] border-2 border-[var(--border)] font-bold text-sm uppercase tracking-wider px-12 py-5 rounded-2xl hover:border-[var(--accent)] transition-all inline-flex items-center gap-6 group/final cursor-pointer shadow-sm">
                        <span className="group-hover:-translate-y-2 transition-transform duration-500">↑</span>
                        Close Report / Return to Dashboard
                        <span className="group-hover:-translate-y-2 transition-transform duration-500">↑</span>
                    </button>
                </div>
            )}
        </div>
    );
}
